interface Props {
  show: boolean;
  onToggle: () => void;
  focusedIndex: number | null;
}

const SHORTCUT_GROUPS = [
  {
    label: 'Search & Filter',
    items: [
      { keys: ['/'], desc: 'Focus search bar' },
      { keys: ['Esc'], desc: 'Clear filters / unfocus' },
    ],
  },
  {
    label: 'Grid Navigation',
    items: [
      { keys: ['←', '→'], desc: 'Move left / right' },
      { keys: ['↑', '↓'], desc: 'Move up / down' },
      { keys: ['Enter'], desc: 'Open preview' },
    ],
  },
  {
    label: 'Card Actions',
    items: [
      { keys: ['F'], desc: 'Toggle favorite' },
      { keys: ['C'], desc: 'Toggle compare' },
      { keys: ['?'], desc: 'Show / hide this panel' },
    ],
  },
];

function Kbd({ label }: { label: string }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-md text-[10px] font-bold font-mono bg-gray-100 border border-gray-300 text-gray-600 leading-none">
      {label}
    </kbd>
  );
}

export default function KeyboardShortcutsPanel({ show, onToggle, focusedIndex }: Props) {
  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3 pointer-events-none">

      {/* Floating panel */}
      <div
        className={`pointer-events-auto bg-white border border-gray-200 rounded-2xl w-64 overflow-hidden transition-all duration-200 origin-bottom-right ${
          show
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 translate-y-2 pointer-events-none'
        }`}
        style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2">
            <i className="ri-keyboard-line text-gray-500 text-sm"></i>
            <span className="text-xs font-bold text-gray-700">Keyboard Shortcuts</span>
          </div>
          {focusedIndex !== null && (
            <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold">
              #{focusedIndex + 1} focused
            </span>
          )}
        </div>

        {/* Shortcut groups */}
        <div className="p-3 space-y-3">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 px-1">{group.label}</p>
              <div className="space-y-1">
                {group.items.map(({ keys, desc }) => (
                  <div key={desc} className="flex items-center justify-between gap-3 px-2 py-1 rounded-lg hover:bg-gray-50">
                    <span className="text-xs text-gray-600">{desc}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {keys.map((k) => <Kbd key={k} label={k} />)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
          <p className="text-[10px] text-gray-400 text-center">
            Works when no modal is open
          </p>
        </div>
      </div>

      {/* Trigger button */}
      <button
        onClick={onToggle}
        className={`pointer-events-auto w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 cursor-pointer border ${
          show
            ? 'bg-violet-500 text-white border-violet-500'
            : focusedIndex !== null
              ? 'bg-violet-50 border-violet-400 text-violet-600'
              : 'bg-white text-gray-500 border-gray-200 hover:border-violet-400 hover:text-violet-500 hover:bg-violet-50'
        }`}
        title="Keyboard shortcuts (?)"
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      >
        <i className="ri-keyboard-line text-base"></i>
      </button>
    </div>
  );
}
